import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const { FiTwitter, FiLinkedin, FiGithub, FiMail, FiMapPin, FiPhone, FiCalendar, FiArrowRight } = FiIcons;

const Footer = () => {
  const [colorIndex, setColorIndex] = useState(0);
  const location = useLocation();

  // Check if current page is Quinformation
  const isQuinformationPage = location.pathname === '/quinformation';

  // Define the color scheme for animation - only for Quinformation page
  const colors = {
    orange: '#FF6A00',
    blue: '#00F0FF',
    purple: '#D94EFF'
  };

  // Animated color effect - only for Quinformation page
  useEffect(() => {
    if (isQuinformationPage) {
      const interval = setInterval(() => {
        setColorIndex(prev => (prev + 1) % 100);
      }, 60); // Update frequently for smooth transitions
      return () => clearInterval(interval);
    }
  }, [isQuinformationPage]);

  // Interpolate between colors for smooth transitions
  const getInterpolatedColor = () => {
    // Create smooth transitions between the three colors
    if (colorIndex < 33) {
      // Transition from orange to blue
      const ratio = colorIndex / 33;
      return interpolateColor(colors.orange, colors.blue, ratio);
    } else if (colorIndex < 66) {
      // Transition from blue to purple
      const ratio = (colorIndex - 33) / 33;
      return interpolateColor(colors.blue, colors.purple, ratio);
    } else {
      // Transition from purple to orange
      const ratio = (colorIndex - 66) / 34;
      return interpolateColor(colors.purple, colors.orange, ratio);
    }
  };

  // Helper function to interpolate between two colors
  const interpolateColor = (color1, color2, ratio) => {
    const hex = (x) => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);

    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);

    const r = r1 + ratio * (r2 - r1);
    const g = g1 + ratio * (g2 - g1);
    const b = b1 + ratio * (b2 - b1);

    return `#${hex(r)}${hex(g)}${hex(b)}`;
  };

  const socialLinks = [
    { icon: FiTwitter, href: '#', label: 'Twitter' },
    { icon: FiLinkedin, href: '#', label: 'LinkedIn' },
    { icon: FiGithub, href: '#', label: 'GitHub' },
    { icon: FiMail, href: '#', label: 'Email' }
  ];

  const footerLinks = [
    {
      title: 'Solutions',
      links: [
        { name: 'IdealMe', href: '#' },
        { name: 'Performance Analytics', href: '#' },
        { name: 'AI Coaching Engine', href: '#' },
        { name: 'On-Demand AI Training', href: '#' },
        { name: 'Asynchronous Modules', href: '#' },
        { name: 'Privacy-First APIs', href: '#' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'Our Team', href: '/about/team' },
        { name: 'Contact', href: '/about/contact' },
        { name: 'Careers', href: '#' },
        { name: 'Blog', href: '#' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Documentation', href: '#' },
        { name: 'API Reference', href: '#' },
        { name: 'Support', href: '#' },
        { name: 'Status', href: '#' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { name: 'Privacy Policy', href: '#' },
        { name: 'Terms of Service', href: '#' },
        { name: 'Cookie Policy', href: '#' },
        { name: 'Security', href: '#' }
      ]
    }
  ];

  const roadmapItems = [
    { date: 'Q4 2025', title: 'AI-augmented coaching modules' },
    { date: 'Q1 2026', title: 'Release of asynchronous training library' },
    { date: 'Q2 2026', title: 'Enterprise dashboard integration' },
    { date: 'Q3 2026', title: 'Mobile app companion release' },
    { date: 'Q4 2026', title: 'Internationalization (multi-language support)' },
    { date: 'Q1 2027', title: 'Launch of Quinformation – AI-powered integrity tool' }
  ];

  // Get the current animated color for Quinformation page
  const animatedColor = isQuinformationPage ? getInterpolatedColor() : '#FF6B00';

  // Create gradient with animated colors for Quinformation page
  const animatedGradient = isQuinformationPage
    ? `linear-gradient(to right, ${colors.purple}, ${animatedColor}, ${colors.blue})`
    : 'linear-gradient(135deg, #FF6B00 0%, #FFB366 100%)';

  return (
    <footer
      id="contact"
      className="bg-gray-950 border-t border-gray-800"
      style={isQuinformationPage ? { borderColor: animatedColor } : {}}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-6 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {/* Logo */}
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <img 
                    src="https://cqpenfycgbztkozuwfiu.supabase.co/storage/v1/object/sign/logos/icononly_transparent_nobuffer.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZjlmMTQ4My1mMmM0LTRjZTAtOWIxNi01N2E0Njk3ZWIxYjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvcy9pY29ub25seV90cmFuc3BhcmVudF9ub2J1ZmZlci5wbmciLCJpYXQiOjE3NTQ5MTY2MDUsImV4cCI6MzMyOTA5MTY2MDV9.92E3oR6MA9SmcZKdeU0lsdO-y2OGtrsHF2YwxSCzQDQ" 
                    alt="Quantum Leverage Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-2xl font-bold">Quantum Leverage</span>
              </div>
              <p className="text-gray-300 leading-relaxed">
                Building transformational tech for exponential people. AI-powered tools that scale personal growth and organizational performance.
              </p>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <a
                href="tel:+17754296732"
                className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors"
              >
                <SafeIcon
                  icon={FiPhone}
                  className="w-5 h-5"
                  style={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
                />
                <span>+1 775 429 6732</span>
              </a>
              <a
                href="mailto:info@quantumleverage.com"
                className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors"
              >
                <SafeIcon
                  icon={FiMail}
                  className="w-5 h-5"
                  style={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
                />
                <span>info@quantumleverage.com</span>
              </a>
              <div className="flex items-center space-x-3 text-gray-300">
                <SafeIcon
                  icon={FiMapPin}
                  className="w-5 h-5"
                  style={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
                />
                <span>Fishers, IN</span>
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              viewport={{ once: true }}
              className="flex space-x-4"
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 bg-gray-800 hover:bg-primary-orange flex items-center justify-center transition-all duration-200 group"
                  aria-label={social.label}
                >
                  <SafeIcon
                    icon={social.icon}
                    className="w-5 h-5 text-gray-300 group-hover:text-black transition-colors"
                  />
                </motion.a>
              ))}
            </motion.div>
          </div>

          {/* Footer Links */}
          <div className="lg:col-span-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {footerLinks.map((section, sectionIndex) => (
              <motion.div
                key={sectionIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-4"
              >
                <h3 className="text-lg font-semibold text-white">{section.title}</h3>
                <ul className="space-y-3">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <motion.a
                        href={link.href}
                        whileHover={{ x: 5 }}
                        className="text-gray-300 hover:text-primary-orange transition-all duration-200"
                      >
                        {link.name}
                      </motion.a>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Roadmap Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 glass-effect p-8"
          style={isQuinformationPage ? { borderColor: animatedColor } : {}}
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
              <h3 className="text-2xl font-bold flex items-center">
                <SafeIcon
                  icon={FiCalendar}
                  className="w-6 h-6 mr-2"
                  style={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
                />
                Product{' '}
                <span
                  className="ml-2 bg-clip-text text-transparent"
                  style={{
                    backgroundImage: isQuinformationPage
                      ? animatedGradient
                      : 'linear-gradient(135deg, #FF6B00 0%, #FFB366 100%)'
                  }}
                >
                  Roadmap
                </span>
              </h3>
              <p className="text-gray-300 mt-2">Our vision for the future of transformation technology</p>
            </div>
            <motion.a
              href="/quinformation"
              whileHover={{ scale: 1.05, x: 5 }}
              className="flex items-center space-x-2 hover:text-white transition-colors mt-4 md:mt-0"
              style={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
            >
              <span>View Quinformation Preview</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </motion.a>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roadmapItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className={`bg-gray-900/50 p-6 border border-gray-800 relative overflow-hidden group ${
                  index === roadmapItems.length - 1 ? 'bg-gradient-to-br from-gray-900 to-gray-800' : ''
                }`}
                style={index === roadmapItems.length - 1 && isQuinformationPage ? { borderColor: animatedColor } : {}}
              >
                <div
                  className="absolute top-0 left-0 h-full w-1 opacity-30 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      isQuinformationPage && index === roadmapItems.length - 1 ? animatedColor : '#FF6B00'
                  }}
                ></div>
                <div className="space-y-2">
                  <div
                    className="font-semibold"
                    style={{
                      color: isQuinformationPage && index === roadmapItems.length - 1 ? animatedColor : '#FF6B00'
                    }}
                  >
                    {item.date}
                  </div>
                  <h4 className="text-lg font-bold group-hover:text-primary-orange transition-colors">
                    {item.title}
                  </h4>
                </div>
                {index === roadmapItems.length - 1 && (
                  <div
                    className="absolute -bottom-2 -right-2 w-24 h-24 rounded-full blur-xl"
                    style={{
                      background: isQuinformationPage ? `${animatedColor}10` : '#FF6B00/10'
                    }}
                  ></div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          viewport={{ once: true }}
          className="border-t pt-8 mt-16"
          style={isQuinformationPage ? { borderColor: animatedColor } : { borderColor: '#2A2A2A' }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2024 Quantum Leverage. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <motion.a
                href="#"
                whileHover={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
                className="hover:text-primary-orange transition-colors"
              >
                Privacy Policy
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
                className="hover:text-primary-orange transition-colors"
              >
                Terms of Service
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ color: isQuinformationPage ? animatedColor : '#FF6B00' }}
                className="hover:text-primary-orange transition-colors"
              >
                Cookie Settings
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tech Accent Line */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        transition={{ duration: 2 }}
        viewport={{ once: true }}
        className="h-px"
        style={{
          background: isQuinformationPage
            ? `linear-gradient(to right, transparent, ${animatedColor}, transparent)`
            : 'linear-gradient(to right, transparent, #FF6B00, transparent)',
          boxShadow: isQuinformationPage ? `0 0 10px ${animatedColor}` : 'none'
        }}
      />
    </footer>
  );
};

export default Footer;